// src/components/FacilityListingsSwipe.jsx

import React, { useContext, useEffect, useRef, useState } from 'react';
import { Container, Spinner, Alert, Button } from 'react-bootstrap';
import { useSwipeable } from 'react-swipeable';
import { motion } from 'framer-motion';
import FlippableListingCard from './FlippableListingCard';
import { AuthContext } from '../../contexts/AuthContext';
import matchingService from '../../services/matchingService';
import './FacilityListingsSwipe.css';
import foilPattern from '../../assets/images/foil-pattern.png';

const PAGE_SIZE = 10;
const ROTATION_ANGLES = [0, -2, 2, -1, 1];

export default function FacilityListingsSwipe() {
  const { auth } = useContext(AuthContext);
  const [listings, setListings]       = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [page, setPage]               = useState(1);
  const [hasMore, setHasMore]         = useState(true);
  const [loading, setLoading]         = useState(true);
  const [errorLoading, setErrorLoading] = useState(false);
  const [deltaX, setDeltaX]           = useState(0);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [swipeHistory, setSwipeHistory] = useState([]);
  const cacheRef = useRef({});
  
  // Track whether this is the first mount
  const isFirstMount = useRef(true);
  
  // fetch listings
  useEffect(() => {
    const fetchListings = async () => {
      // Skip if no auth token or page is already cached
      if (!auth?.token || cacheRef.current[page]) {
        return;
      }
      
      // If this is not the initial mount but we're still on page 1, 
      // that means we're dealing with a double-fetch in development mode
      if (!isFirstMount.current && page === 1) {
        console.log('Preventing duplicate API call on page load');
        return;
      }
      
      setLoading(true);
      console.log(`Fetching listings for page ${page}`);
      
      try {
        const res = await fetch(
          `${process.env.REACT_APP_RXROOSTER_BACKEND_URL}/listings/matches?page=${page}&limit=${PAGE_SIZE}`,
          { headers: { Authorization: `Bearer ${auth.token}` } }
        );
        
        if (!res.ok) throw new Error('Failed to fetch listings');
        
        const { facilities = [] } = await res.json();
        console.log(`Received ${facilities.length} listings for page ${page}`);
        
        if (facilities.length === 0) {
          setHasMore(false);
        } else {
          // Important: REPLACE the listings for the first page rather than appending
          // This fixes the rendering issue when multiple API responses come in
          if (page === 1) {
            cacheRef.current[page] = facilities;
            setListings(facilities); // Replace completely for page 1
          } else {
            cacheRef.current[page] = facilities;
            setListings(prev => [...prev, ...facilities]); // Append for other pages
          }
        }
      } catch (error) {
        console.error('Error fetching listings:', error);
        setErrorLoading(true);
        setHasMore(false);
      } finally {
        // Mark this as not the first mount anymore
        isFirstMount.current = false;
        setLoading(false);
      }
    };
    
    fetchListings();
  }, [auth, page]);

  useEffect(() => {
    if (currentIndex >= listings.length && hasMore && !loading) {
      setPage(p => p + 1);
    }
  }, [currentIndex, listings.length, hasMore, loading]);

  // advance card on any swipe and send to backend
  const handleSwipe = async (dir) => {
    const currentListing = listings[currentIndex];
    if (!currentListing) return;
    
    // Log the entire listing object to inspect its structure
    console.log('Current listing object:', currentListing);
    
    const swipeDir = dir === 'Right' ? 'right' : 'left';
    
    try {
      // Record swipe action in history for UI feedback
      setSwipeHistory(prev => [...prev, { listing: currentListing, direction: swipeDir }]);
      
      // Only send swipe to backend if we have auth token and listing ID
      if (auth?.token && currentListing._id) {
        console.log(`Sending ${swipeDir} swipe for listing ${currentListing._id}`);
        
        await matchingService.providerSwipe(
          currentListing._id,
          swipeDir
        );
      } else {
        console.warn('Missing data for backend swipe:', {
          hasToken: !!auth?.token,
          listingId: currentListing._id
        });
      }
    } catch (error) {
      console.error('Failed to record swipe in database:', error);
    } finally {
      // Always update UI regardless of API success
      setSwipeDirection(dir);
      setTimeout(() => {
        setSwipeDirection(null);
        setCurrentIndex(i => i + 1);
        setDeltaX(0);
      }, 300);
    }
  };

  const handlers = useSwipeable({
    onSwipedLeft:  () => handleSwipe('Left'),
    onSwipedRight: () => handleSwipe('Right'),
    onSwiping:     ({ deltaX }) => setDeltaX(deltaX),
    onSwiped:      () => setDeltaX(0),
    trackMouse:    true,
  });

  // top-card flying logic
  const getTopCardStyle = () => {
    const rotate = deltaX / 20;
    const flyAway =
      swipeDirection === 'Right'
        ? 'translateX(100vw) rotate(45deg)'
        : swipeDirection === 'Left'
        ? 'translateX(-100vw) rotate(-45deg)'
        : `translateX(${deltaX}px) rotate(${rotate}deg)`;

    return {
      transform: `translate(-50%, -50%) ${flyAway}`,
      transition: 'transform 0.3s ease-out',
      zIndex: listings.length - currentIndex,
    };
  };

  const getStackedCardStyle = idx => {
    const offsetY = 12 * idx;
    const scale   = 1 - 0.04 * idx;
    const rot     = ROTATION_ANGLES[idx] || 0;
    return {
      transform: `translate(-50%, -50%) translateY(${offsetY}px) scale(${scale}) rotate(${rot}deg)`,
      transition: 'transform 0.3s ease-out',
      zIndex: listings.length - currentIndex - idx,
    };
  };

  const getOverlayStyle = () => ({
    backgroundColor:
      deltaX > 50  ? 'rgba(38,194,129,0.2)' :
      deltaX < -50 ? 'rgba(229,57,53,0.2)' :
      'transparent',
  });

  // states
  if (loading && page === 1) 
    return <Spinner animation="border" />;
  if (errorLoading && !listings.length) 
    return <Alert variant="danger">Failed to load.</Alert>;
  if (!hasMore && currentIndex >= listings.length) {
    return (
      <div className="celebration-screen">
        <h3>🎉 All CRNA matches seen!</h3>
        <p>{swipeHistory.filter(s => s.direction === 'right').length} Interested</p>
        <p>{swipeHistory.filter(s => s.direction === 'left').length} Not Interested</p>
        <Button onClick={() => { /* your notify logic */ }}>Notify Me</Button>
      </div>
    );
  }

  return (
    <Container
      className="d-flex flex-column align-items-center justify-content-center"
      style={{ minHeight: '100vh', '--foil-pattern': `url(${foilPattern})` }}
    >
      <div {...handlers} className="deck-wrapper">
        {listings.slice(currentIndex, currentIndex + 5).map((facility, idx) => {
          let cardStyle = idx === 0
            ? getTopCardStyle()
            : getStackedCardStyle(idx);

          // lift underlying cards on drag
          if (idx > 0 && deltaX) {
            cardStyle.transform = cardStyle.transform.replace(
              /translateY\(([-\d]+)px\)/,
              (m, y) => `translateY(${parseInt(y) - 8}px)`
            );
          }

          return (
            <motion.div
              key={facility._id || facility.id}
              className={`deck-card${idx === 0 ? ' top-card' : ''}`}
              layout
              style={cardStyle}
            >
              <FlippableListingCard profile={facility} isCertified={idx === 0} />
              {idx === 0 && <div className="swipe-overlay" style={getOverlayStyle()} />}
            </motion.div>
          );
        })}
      </div>
    </Container>
  );
}
